'use client'

import { useEffect, useState } from 'react'
import { AlertTriangle, ChevronDown, ChevronUp, Sparkles } from 'lucide-react'
import { CardContent, CardHeader, CardTitle, CardV2 } from '../CardV2/CardV2'
import { TextareaV2 } from '../TextareaV2/TextareaV2'
import { ButtonV2 } from '../ButtonV2/ButtonV2'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../Collapsible/Collapsible'
import OrderForm from '../OrderForm/OrderForm'
import OrdersAPI, { Order } from '../../api/OrdersAPI'
import { useTranslation } from 'react-i18next'

type MatchingCatalogue = {
  name: string
  uuid: string
}

type APIResponse = {
  orders: Order[]
  matchingCatalogues: MatchingCatalogue[]
}

type Props = {
    order: Order | null,
    onOrderChange?: (value: Order) => void,
    isDisabled?: boolean,
    actionButton?: JSX.Element,
    onValidationChange?: (isValid: boolean) => void
};

const examples = [
  "orders.orders_basket.ai_form.examples.first",
  "orders.orders_basket.ai_form.examples.second",
  "orders.orders_basket.ai_form.examples.third",
]

export default function NewOrderForm({ order, onOrderChange = () => null, onValidationChange }: Props) {
  const [, setIsPlainText] = useState(false)
  const [plainTextRequest, setPlainTextRequest] = useState('')
  const [apiResponse, setApiResponse] = useState<APIResponse | null>(null)
  const [isExampleOpen, setIsExampleOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isGenerateDisabled, setIsGenerateDisabled] = useState(true)
  const { t } = useTranslation();

  useEffect(() => {
    setIsGenerateDisabled(plainTextRequest.length < 3)
  }, [plainTextRequest])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      const response: any = await OrdersAPI.generateOrder(plainTextRequest);
      setApiResponse(response.data);
      if (response?.data?.orders) {
        setIsPlainText(false);
        const mergedOrder = mergeOrders(order, response.data.orders[0]);
        onOrderChange(mergedOrder);
      }
    } catch (error) {
      console.error('Error generating order:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleImportCatalogue = (uuid: string) => {
    window.open(`${window.location.protocol}//${window.location.host}/catalogues/${uuid}`, '_blank');
  }

  const handleOrderChange = (updatedOrder: Order) => {
    onOrderChange(updatedOrder);
  };

  const mergeOrders = (existingOrder: Order | null, apiOrder: Order): Order => {
    return {
      ...existingOrder,
      ...apiOrder,
    }
  }

  return (
    <>
        {/* TODO: temporary */}
        {false ? (
          <form onSubmit={handleSubmit} className="space-y-4 pt-5">
            <TextareaV2
              placeholder={t("orders.orders_basket.ai_form.text")!}
              value={plainTextRequest}
              onChange={(e) => setPlainTextRequest(e.target.value)}
              className="min-h-[100px]"
            />
            <Collapsible open={isExampleOpen} onOpenChange={setIsExampleOpen}>
              <CollapsibleTrigger asChild>
                <ButtonV2 variant="outline" className="flex items-center gap-2">
                  {isExampleOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  { t("orders.orders_basket.ai_form.examples") }
                </ButtonV2>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 space-y-2 bg-white border border-gray-200 rounded-md p-4 shadow-sm">
                {examples.map((example, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors duration-200">
                    <p className="text-sm text-gray-700 leading-relaxed">
                        <Sparkles className="inline-block mr-2 h-4 w-4 text-blue-500" />
                        {t(example)}
                    </p>
                    </div>
                ))}
              </CollapsibleContent>
            </Collapsible>
            <div className="flex justify-between">
                <ButtonV2
                type="submit"
                disabled={isGenerateDisabled || isLoading}
                actionName="Generate Order with AI" 
                className={`
                    relative overflow-hidden
                    bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500
                    text-white font-bold py-2 px-4 rounded
                    transition-all duration-300 ease-in-out
                    transform hover:scale-105 hover:shadow-lg
                    ${isLoading ? 'animate-pulse' : ''}
                `}
                >
                  {isLoading ? (
                      <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      { t("orders.orders_basket.ai_form.button.loading") }
                      </span>
                  ) : (
                      <span className="flex items-center">
                      <Sparkles className="mr-2 h-4 w-4" />
                      { t("orders.orders_basket.ai_form.button") }
                      </span>
                  )}
                  <span className="absolute top-0 left-0 w-full h-full bg-white opacity-25 transform scale-x-0 origin-left transition-transform duration-300 ease-out group-hover:scale-x-100"></span>
                </ButtonV2>
              <ButtonV2 type="button" variant="outline" actionName="Switch to Manual Order Form"  onClick={() => setIsPlainText(false)}>
                { t("orders.orders_basket.ai_form.manual_switch") }
              </ButtonV2>
            </div>
          </form>
        ) : (
          <div>
            <OrderForm order={order} onOrderChange={handleOrderChange} onValidationChange={onValidationChange}/>
            {/* TODO: temporary */}
            {/* <ButtonV2 onClick={() => setIsPlainText(true)} actionName="Switch to AI Order Form">
              { t("orders.orders_basket.ai_form.text_switch") }
            </ButtonV2> */}
          </div>
        )}

      {apiResponse && (
        <div className="mt-8 space-y-4">
          {apiResponse?.matchingCatalogues?.length > 0 && (
            <CardV2>
              <CardHeader>
                <CardTitle>{t("orders.orders_basket.ai_form.catalogues")}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-start space-x-2 mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-yellow-700">
                    {t("orders.orders_basket.ai_form.catalogues.note", "These other catalogues seem to have the item you are looking for. Have a look at them and import them to start filling orders with them.")}
                  </p>
                </div>
                <ul className="space-y-2">
                  {apiResponse.matchingCatalogues.map((catalogue) => (
                    <li key={catalogue.uuid} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                      <span className="font-medium">{catalogue.name}</span>
                      <ButtonV2 
                        onClick={() => handleImportCatalogue(catalogue.uuid)}
                        className="text-sm"
                      >
                        {t("orders.orders_basket.ai_form.catalogues.import")}
                      </ButtonV2>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </CardV2>
          )}
        </div>
      )}
    </>
  )
}